/**
 * Chat view model: folds the server's event stream into renderable items.
 * Pure, so it is unit-tested without a browser.
 */
import type { ChatEvent, McpServerState } from '../../../shared/api.js';

export type ApprovalState =
  'pending' | 'approved' | 'declined' | 'timeout' | 'interrupted' | 'session closed';

export interface ToolItem {
  kind: 'tool';
  key: string;
  toolUseId: string | undefined;
  name: string;
  input: unknown;
  result: { isError: boolean; text: string } | undefined;
  approval: { requestId: string; state: ApprovalState; title: string | undefined } | undefined;
}

export type ChatItem =
  | { kind: 'user'; key: string; text: string }
  | { kind: 'assistant'; key: string; text: string; streaming: boolean }
  | ToolItem
  | { kind: 'notice'; key: string; tone: 'info' | 'warning' | 'error'; text: string };

export interface RetryState {
  attempt: number;
  maxRetries: number;
  delayMs: number;
  status: number | null;
  error: string;
}

export interface ChatState {
  items: ChatItem[];
  lastSeq: number;
  busy: boolean;
  closed: boolean;
  model: string | undefined;
  mcpServers: McpServerState[];
  lastTurn: { durationMs: number; totalCostUsd: number } | undefined;
  /** Set while the SDK is retrying an API request; cleared by the next output. */
  retry: RetryState | undefined;
}

export const initialChatState: ChatState = {
  items: [],
  lastSeq: -1,
  busy: false,
  closed: false,
  model: undefined,
  mcpServers: [],
  lastTurn: undefined,
  retry: undefined,
};

function finishStreaming(items: ChatItem[]): ChatItem[] {
  const last = items.at(-1);
  if (last?.kind === 'assistant' && last.streaming) {
    return [...items.slice(0, -1), { ...last, streaming: false }];
  }
  return items;
}

function updateTool(
  items: ChatItem[],
  match: (item: ToolItem) => boolean,
  update: (item: ToolItem) => ToolItem,
): ChatItem[] | undefined {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    const item = items[i];
    if (item?.kind === 'tool' && match(item)) {
      const next = items.slice();
      next[i] = update(item);
      return next;
    }
  }
  return undefined;
}

const TURN_ERRORS: Record<string, string> = {
  error_max_budget_usd: 'Достигнут лимит бюджета сессии.',
  error_max_turns: 'Достигнут лимит шагов агента.',
  error_during_execution: 'Ответ прерван.',
};

export function applyChatEvent(state: ChatState, event: ChatEvent): ChatState {
  if (event.seq <= state.lastSeq) return state;
  const key = String(event.seq);
  // Any event other than a retry means the request went through (or finally failed).
  const base: ChatState = { ...state, lastSeq: event.seq, retry: undefined };

  switch (event.type) {
    case 'session_ready':
      return { ...base, model: event.model, mcpServers: event.mcpServers };

    case 'api_retry':
      return {
        ...base,
        retry: {
          attempt: event.attempt,
          maxRetries: event.maxRetries,
          delayMs: event.delayMs,
          status: event.status,
          error: event.error,
        },
      };

    case 'notice':
      return {
        ...base,
        retry: state.retry,
        items: [
          ...state.items,
          {
            kind: 'notice',
            key,
            tone: event.level === 'info' ? 'info' : 'warning',
            text: event.text,
          },
        ],
      };

    case 'user_message':
      return {
        ...base,
        items: [...finishStreaming(state.items), { kind: 'user', key, text: event.text }],
      };

    case 'turn_started':
      return { ...base, busy: true };

    case 'text_delta': {
      const last = state.items.at(-1);
      if (last?.kind === 'assistant' && last.streaming) {
        return {
          ...base,
          items: [...state.items.slice(0, -1), { ...last, text: last.text + event.text }],
        };
      }
      return {
        ...base,
        items: [...state.items, { kind: 'assistant', key, text: event.text, streaming: true }],
      };
    }

    case 'assistant_text': {
      const last = state.items.at(-1);
      if (last?.kind === 'assistant' && last.streaming) {
        return {
          ...base,
          items: [...state.items.slice(0, -1), { ...last, text: event.text, streaming: false }],
        };
      }
      return {
        ...base,
        items: [...state.items, { kind: 'assistant', key, text: event.text, streaming: false }],
      };
    }

    case 'tool_use': {
      const items = finishStreaming(state.items);
      const merged = updateTool(
        items,
        (item) => item.toolUseId === event.toolUseId,
        (item) => ({ ...item, name: event.name, input: event.input }),
      );
      return {
        ...base,
        items: merged ?? [
          ...items,
          {
            kind: 'tool',
            key,
            toolUseId: event.toolUseId,
            name: event.name,
            input: event.input,
            result: undefined,
            approval: undefined,
          },
        ],
      };
    }

    case 'tool_result': {
      const items = updateTool(
        state.items,
        (item) => item.toolUseId === event.toolUseId,
        (item) => ({ ...item, result: { isError: event.isError, text: event.text } }),
      );
      return items ? { ...base, items } : base;
    }

    case 'approval_request': {
      const approval = {
        requestId: event.requestId,
        state: 'pending' as const,
        title: event.title,
      };
      const items = finishStreaming(state.items);
      const merged = event.toolUseId
        ? updateTool(
            items,
            (item) => item.toolUseId === event.toolUseId,
            (item) => ({ ...item, approval }),
          )
        : undefined;
      return {
        ...base,
        items: merged ?? [
          ...items,
          {
            kind: 'tool',
            key,
            toolUseId: event.toolUseId,
            name: event.name,
            input: event.input,
            result: undefined,
            approval,
          },
        ],
      };
    }

    case 'approval_resolved': {
      const state_ = event.reason as ApprovalState;
      const items = updateTool(
        state.items,
        (item) => item.approval?.requestId === event.requestId,
        (item) =>
          item.approval ? { ...item, approval: { ...item.approval, state: state_ } } : item,
      );
      return items ? { ...base, items } : base;
    }

    case 'turn_finished': {
      const items = finishStreaming(state.items);
      const notice: ChatItem[] = event.ok
        ? []
        : [
            {
              kind: 'notice',
              key,
              tone: 'error',
              text: [
                TURN_ERRORS[event.subtype] ?? `Ошибка: ${event.subtype}`,
                ...event.errors,
              ].join(' '),
            },
          ];
      return {
        ...base,
        busy: false,
        items: [...items, ...notice],
        lastTurn: { durationMs: event.durationMs, totalCostUsd: event.totalCostUsd },
      };
    }

    case 'error':
      return {
        ...base,
        items: [
          ...finishStreaming(state.items),
          { kind: 'notice', key, tone: 'error', text: event.message },
        ],
      };

    case 'session_closed':
      return {
        ...base,
        busy: false,
        closed: true,
        items: [
          ...finishStreaming(state.items),
          { kind: 'notice', key, tone: 'info', text: `Сессия завершена: ${event.reason}.` },
        ],
      };
  }
}

export function pendingApprovals(state: ChatState): ToolItem[] {
  return state.items.filter(
    (item): item is ToolItem => item.kind === 'tool' && item.approval?.state === 'pending',
  );
}
